import React, { useState } from "react";

import PopupComponent from "@components/PopupComponent";
import EditPopup from "./EditPopup";
import TerminalPopup from "./TerminalPopup";
import { useTranslation } from "react-i18next";

import { ONE_VALUE } from "@utils/constants";

const FIRST_LEVEL = 0;
const NO_INDEX = -1;

const CustomPopup = ({ selectedData, onSave, setPopupOpen, isNew }) => {
  const { t: translate, i18n } = useTranslation();

  const { data, type, action, parent, originalData, dataId, transformedData } =
    selectedData;
  
  // Ensure data has all required fields
  const initializeData = (data) => {
    return {
      ...data,
      OrgDetails: data.OrgDetails || [],
      OrgAttributes: data.OrgAttributes || [],
      OrgContacts: data.OrgContacts || [],
      OrgAddresses: data.OrgAddresses || [],
    };
  };
  
  const [newData, setNewData] = useState(initializeData(data));
  const [attributes, setAttributes] = useState(
    data.OrgAttributes?.map((attr) => attr.AttributeValue) || []
  );
  const [inputValue, setInputValue] = useState("");

  const handleInputChange = (field, value) => {
    setNewData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAttributeInput = (e) => {
    const value = e.target.value;
    if (value.includes(",")) {
      const newAttribute = value.split(",")[FIRST_LEVEL].trim();
      if (newAttribute && !attributes.includes(newAttribute)) {
        setAttributes((prev) => [...prev, newAttribute]);
        newData.OrgAttributes.push({
          AttributeTypeTag: "ORG_EXTERNAL_CODE",
          AttributeValue: newAttribute,
          AttributeTypeDetails: [
            {
              LanguageCode: "en",
              Name: "Org External Code",
            },
          ],
        });
        handleInputChange("OrgAttributes", newData.OrgAttributes);
        setInputValue("");
      }
    } else {
      setInputValue(value);
    }
  };

  const handleRemoveAttribute = (index, attribute) => {
    setAttributes((prev) => prev.filter((_, i) => i !== index));
    const indexToRemove = newData.OrgAttributes.findIndex(
      (item) => item.AttributeValue === attribute
    );
    if (indexToRemove !== NO_INDEX) {
      newData.OrgAttributes.splice(indexToRemove, ONE_VALUE);
    }
  };

  const handleSave = () => {
    onSave(newData, isNew);
  };

  const renderDynamicContent = () => {
    switch (type) {
      case "branch":
        return (
          <EditPopup
            newData={newData}
            attributes={attributes}
            inputValue={inputValue}
            handleInputChange={handleInputChange}
            handleAttributeInput={handleAttributeInput}
            handleRemoveAttribute={handleRemoveAttribute}
            handleSave={handleSave}
            setPopupOpen={setPopupOpen}
            isNew={action === "add"}
            hasHeader={true}
            parent={parent}
            parentName="Region"
            type={type}
          />
        );
      case "outlet":
        return (
          <EditPopup
            newData={newData}
            attributes={attributes}
            inputValue={inputValue}
            handleInputChange={handleInputChange}
            handleAttributeInput={handleAttributeInput}
            handleRemoveAttribute={handleRemoveAttribute}
            handleSave={handleSave}
            setPopupOpen={setPopupOpen}
            isNew={action === "add"}
            type={type}
          />
        );
      case "terminal":
        return (
          <TerminalPopup
            newData={newData}
            attributes={attributes}
            inputValue={inputValue}
            handleInputChange={handleInputChange}
            handleAttributeInput={handleAttributeInput}
            handleRemoveAttribute={handleRemoveAttribute}
            handleSave={handleSave}
            setPopupOpen={setPopupOpen}
            isNew={action === "add"}
            hasHeader={() => {
              const parentName = "Region";
              return (
                <div className="mb-4 bg-gray-100 p-4 rounded-lg flex flex-col gap-6 dark:bg-gray-900 dark:text-white">
                  <div className="flex items-center justify-between">
                    {parent.OrgDetails?.map((detail, index) => (
                      <div
                        key={index}
                        data-testid={`terminal-popup-${
                          detail.ShortName
                        }-${detail.LanguageCode.toUpperCase()}`}
                        className="text-sm flex flex-col gap-2"
                      >
                        <div className="font-semibold">
                          {parentName} {translate("hierarchy.name")} {"("}{detail.LanguageCode.toUpperCase()}
                          {")"}
                        </div>
                        <div>{detail.ShortName || "N/A"}</div>
                      </div>
                    ))}
                  </div>
                  <div className="text-sm flex flex-col gap-2">
                    <div
                      data-testid={`terminal-popup-outlet-${transformedData.name}`}
                      className="font-semibold"
                    >
                      {translate("hierarchy.outlet-name")}
                    </div>
                    <div>{transformedData.name || "N/A"}</div>
                  </div>
                </div>
              );
            }}
            parent={parent}
            parentName="Region"
            type={type}
            dataId={dataId}
          />
        );
      default:
        return <div></div>;
    }
  };

  return (
    <PopupComponent
      externalClassName={"w-[40vw] max-h-[75dvh] top-0"}
      open={true}
      rawValue={renderDynamicContent()}
      setPopupOpen={(value) => setPopupOpen(value)}
      addBlur
    />
  );
};

export default CustomPopup;
